import { create } from "zustand";
import {
  persist,
  type PersistStorage,
  type StorageValue,
} from "zustand/middleware";

type CookiesType = {
  necessary: true;
};

type CookiesConsentStoreType = {
  consent: Map<string, CookiesType>;
  setConsent: (userId: string, newConsent: CookiesType) => void;
};

// Custom storage that handles Map serialization
const mapStorage: PersistStorage<CookiesConsentStoreType> = {
  getItem: (name: string): StorageValue<CookiesConsentStoreType> | null => {
    try {
      const value = localStorage.getItem(name);
      if (!value) return null;
      const data = JSON.parse(value) as {
        state: { consent: [string, CookiesType][] };
        version: number;
      };
      return {
        state: {
          consent: new Map(data.state.consent || []),
          setConsent: () => {
            // This will be replaced by the actual store implementation
          },
        } as CookiesConsentStoreType,
        version: data.version,
      };
    } catch {
      return null;
    }
  },
  setItem: (
    name: string,
    value: StorageValue<CookiesConsentStoreType>,
  ): void => {
    try {
      localStorage.setItem(
        name,
        JSON.stringify({
          ...value,
          state: {
            ...value.state,
            consent: Array.from(value.state.consent.entries()),
          },
        }),
      );
    } catch {
      // Fallback to regular storage
      localStorage.setItem(name, JSON.stringify(value));
    }
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

const useCookieConsentStore = create<CookiesConsentStoreType>()(
  persist(
    (set) => ({
      consent: new Map(),
      setConsent: (userId: string, newConsent: CookiesType) =>
        set((prev) => ({
          consent: new Map(prev.consent).set(userId, newConsent),
        })),
    }),
    {
      name: `cookie-consent-store`,
      version: 2,
      storage: mapStorage,
    },
  ),
);

export {
  useCookieConsentStore,
  type CookiesType,
  type CookiesConsentStoreType,
};
